import type { Card } from "../domain/types";
import type { Language, ModeId, Phase } from "../domain/enums";
import { PHASE_ORDER } from "../domain/enums";
import { mulberry32, shuffleInPlace } from "./rng";

/**
 * CardQueue (§3.2).
 *
 * Build a deterministic, phase-grouped, language-filtered queue of card IDs
 * for a session. The order is computed once from (packCards, language, seed)
 * and is stable across reloads, satisfying §3.2's stable-shuffle requirement.
 *
 * The runtime "consumed" set lives on the session — this module only knows
 * how to compute the canonical order.
 */
export function buildQueue(args: {
  cards: readonly Card[];
  packId: string;
  language: Language;
  seed: number;
}): string[] {
  const filtered = args.cards.filter(
    (c) =>
      c.packId === args.packId &&
      c.language === args.language &&
      // Reward / penalty cards live in a side pool — never appear in the queue.
      (c.kind ?? "main") === "main"
  );
  // Group by phase, shuffle within group with the same seed (offset per phase),
  // then flatten warmup → engagement → peak.
  const byPhase: Record<Phase, Card[]> = { warmup: [], engagement: [], peak: [] };
  for (const c of filtered) byPhase[c.phase].push(c);

  const ordered: Card[] = [];
  const phases: Phase[] = ["warmup", "engagement", "peak"];
  // Sort phases by their declared order to be safe (in case of future renames).
  phases.sort((a, b) => PHASE_ORDER[a] - PHASE_ORDER[b]);

  let phaseSeed = args.seed >>> 0;
  for (const ph of phases) {
    const group = byPhase[ph];
    shuffleInPlace(group, mulberry32(phaseSeed));
    // Anti-cluster pass: rearrange so the same card type never appears 3 times
    // in a row within the phase. Improves variety / pacing for the host.
    interleaveByType(group);
    ordered.push(...group);
    phaseSeed = (phaseSeed + 0x85ebca6b) >>> 0;
  }
  return ordered.map((c) => c.id);
}

/**
 * In-place anti-clustering pass. Walks the array; whenever the previous TWO
 * cards share a type with `current`, look ahead for a different-type card and
 * swap. Produces a queue where no 3 consecutive cards share a type when at
 * least one alternative type is available in the rest of the array.
 *
 * Deterministic — the input order is the seeded shuffle, so two reloads give
 * the same final queue.
 */
function interleaveByType(group: Card[]): void {
  for (let i = 2; i < group.length; i++) {
    const a = group[i - 2]!;
    const b = group[i - 1]!;
    const c = group[i]!;
    if (a.type === b.type && b.type === c.type) {
      // Find the next index with a different type and swap with i.
      for (let j = i + 1; j < group.length; j++) {
        if (group[j]!.type !== c.type) {
          const tmp = group[i]!;
          group[i] = group[j]!;
          group[j] = tmp;
          break;
        }
      }
      // If no alternative exists in the tail, leave it (the rest of the queue
      // is monotype anyway — host will read them as-is).
    }
  }
}

/**
 * Given the canonical queue + the set of consumed card IDs, return the next
 * card ID to serve, or null if the pack is exhausted.
 *
 * The brief says SKIPPED does NOT consume — but we still want a different card
 * to appear next, so this function takes an extra `excludeIds` (transiently
 * set on the session for skip/replace).
 */
export function nextCardId(
  queue: readonly string[],
  consumed: ReadonlySet<string>,
  excludeIds: ReadonlySet<string> = new Set()
): string | null {
  for (const id of queue) {
    if (consumed.has(id)) continue;
    if (excludeIds.has(id)) continue;
    return id;
  }
  // Nothing left after exclusion — fall back to ignoring excludeIds so we
  // never get stuck if the host keeps skipping.
  for (const id of queue) {
    if (consumed.has(id)) continue;
    return id;
  }
  return null;
}

/**
 * Pick a random reward (DONE) or penalty (FAILED) card from the side pool.
 * Filters by mode + language + kind. Deterministic via the seedStream so
 * reload reproduces the same consequence (good UX for "wait, what was that
 * reward?").
 *
 * Phase preference: when `phase` is provided, prefer consequences tagged
 * with that phase. If no consequence in the requested phase exists, fall
 * back to the full pool so the game never breaks on missing content.
 * Lets the host author "warmup-soft" vs "peak-brutal" penalties and have
 * them surface only when the session is in the matching emotional band.
 */
export function pickConsequence(args: {
  cards: readonly Card[];
  modeId: ModeId;
  language: Language;
  kind: "reward" | "penalty";
  seedStream: number;
  /** Current session phase — used as a soft filter on the pool. */
  phase?: Phase;
  /** Card ids already pulled in this session — engine excludes them so
   *  one penalty doesn't pop twice in one evening. */
  excludeIds?: ReadonlySet<string>;
  /**
   * Base id (no `lang:` prefix) of the main card just resolved. When set, and
   * a consequence card carrying `parentCardId === parentMainBaseId` exists in
   * the pack+language+kind slice, that bound consequence is served — bypassing
   * the phase pool entirely. Bound consequences are NEVER served for other
   * main cards: they live in their own private slot.
   */
  parentMainBaseId?: string | null;
}): Card | null {
  const fullPool = args.cards.filter(
    (c) =>
      c.modeId === args.modeId &&
      c.language === args.language &&
      (c.kind ?? "main") === args.kind
  );
  if (fullPool.length === 0) return null;

  const exclude = args.excludeIds ?? new Set<string>();

  // Tier 0: bound consequence — main card has its own private reward/penalty.
  // If we find one matching the just-resolved main, serve it and stop. We
  // intentionally bypass the `excludeIds` check: a bound card that has been
  // pulled before in this session is still THE ONE for this main card. The
  // anti-repeat rule is for the shared pool, not for 1:1 pairings.
  if (args.parentMainBaseId) {
    const bound = fullPool.find((c) => c.parentCardId === args.parentMainBaseId);
    if (bound) return bound;
  }

  // Pool below = legacy shared pool. Bound consequences (parentCardId set)
  // never leak to unrelated main cards — strip them.
  const sharedPool = fullPool.filter((c) => !c.parentCardId);
  if (sharedPool.length === 0) return null;

  // Tier 1: phase-matched AND not yet used → most preferred.
  const phasePool =
    args.phase != null
      ? sharedPool.filter((c) => c.phase === args.phase && !exclude.has(c.id))
      : [];
  // Tier 2: any phase, not yet used.
  const unusedFull = sharedPool.filter((c) => !exclude.has(c.id));
  // Tier 3: pool fully exhausted — fall back to any (game must not break).
  const pool =
    phasePool.length > 0 ? phasePool : unusedFull.length > 0 ? unusedFull : sharedPool;

  const rng = mulberry32(args.seedStream);
  return pool[Math.floor(rng() * pool.length)] ?? null;
}
